import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ehDecimal, formatarDinheiro, formatarFracaoPercentual } from '@/lib/format';
import { ROTULO_FAIXA_SO_COR } from '@/components/profit-badge/profit-badge';

export type ChaveSegmento = 'custo' | 'variavel' | 'fixa' | 'lucro';

export interface SegmentoMargem {
  chave: ChaveSegmento;
  rotulo: string;
  /** Fracao do preco (string decimal vinda da API, ex.: "0.2800"). Define a largura e o percentual da legenda. */
  fracao: string;
  /** Valor monetario do segmento (string decimal vinda da API). Opcional. */
  valor?: string;
}

export interface MarginBarProps {
  /** Ausente sem `lucro.ver` (a API omite as fracoes): a barra vira uma faixa cheia so de cor, com o rotulo de `faixa`. */
  segmentos?: SegmentoMargem[];
  /** Faixa ja decidida pela API; so usada no modo so-cor (sem `segmentos`). */
  faixa?: 'green' | 'orange' | 'red';
  /** Titulo acima da barra. */
  titulo?: string;
  className?: string;
  /** Conteudo extra por segmento, ao lado do percentual (ex.: botao de detalhe). Presentacional. */
  legendaExtra?: Partial<Record<ChaveSegmento, ReactNode>>;
}

/** Cor cheia da faixa na barra so-cor (tokens de cor; o rotulo fica abaixo). */
const COR_BARRA_SO_COR: Record<'green' | 'orange' | 'red', string> = { green: 'bg-success', orange: 'bg-warning-dot', red: 'bg-destructive' };

const COR: Record<ChaveSegmento, string> = {
  custo: 'bg-chart-custo',
  variavel: 'bg-chart-desp-variavel',
  fixa: 'bg-chart-desp-fixa',
  lucro: 'bg-chart-lucro',
};

/**
 * "Para onde vai cada real": barra puramente presentacional. Todas as fracoes chegam prontas da API.
 * Nao ha divisao, soma nem arredondamento aqui: a largura e `calc(fracao * 100%)` resolvido pelo CSS
 * (limitado a 0..100%; fracao invalida ou negativa vira largura 0) e a legenda so reformata a string recebida.
 */
export function MarginBar({ segmentos, faixa, titulo = 'Para onde vai cada real do preço', className, legendaExtra }: MarginBarProps) {
  if (!segmentos) {
    if (!faixa) return null;
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <div className="text-small font-medium">{titulo}</div>
        <div role="img" aria-label={ROTULO_FAIXA_SO_COR[faixa]} data-testid="barra-so-cor" data-faixa={faixa} className={cn('h-3.5 w-full rounded-full', COR_BARRA_SO_COR[faixa])} />
        <p className="text-small text-ink-secondary">{ROTULO_FAIXA_SO_COR[faixa]}</p>
      </div>
    );
  }
  const rotuloAria = segmentos.map((s) => `${s.rotulo} ${formatarFracaoPercentual(s.fracao)}`).join(', ');

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="text-small font-medium">{titulo}</div>
      <div role="img" aria-label={rotuloAria} className="flex h-3.5 overflow-hidden rounded-full bg-line-soft">
        {segmentos.map((s) => (
          <div
            key={s.chave}
            data-segmento={s.chave}
            className={cn('h-full shrink-0 [width:clamp(0%,calc(var(--fracao)*100%),100%)]', COR[s.chave])}
            style={{ '--fracao': ehDecimal(s.fracao) ? s.fracao.trim() : '0' } as CSSProperties}
          />
        ))}
      </div>
      <ul className="grid grid-cols-1 gap-x-5 gap-y-2 text-small sm:grid-cols-2">
        {segmentos.map((s) => (
          <li key={s.chave} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span aria-hidden="true" className={cn('size-2.5 shrink-0 rounded-[3px]', COR[s.chave])} />
            <span className="min-w-0 flex-1 basis-20 [overflow-wrap:anywhere]">{s.rotulo}</span>
            <span className="tabular whitespace-nowrap text-ink-secondary">{formatarFracaoPercentual(s.fracao)}</span>
            {legendaExtra?.[s.chave]}
            {s.valor !== undefined && <b className="tabular ml-auto whitespace-nowrap text-right font-semibold">{formatarDinheiro(s.valor)}</b>}
          </li>
        ))}
      </ul>
    </div>
  );
}
